import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Search, Bell, Sun, Moon, ChevronDown, Building2, User as UserIcon, FileText,
  Users, Settings, LogOut, ExternalLink,
} from "lucide-react";
import { COMPANIES, COMPANY_ORDER, PERSONS, PERSON_ORDER } from "./data";

export type SearchHit =
  | { type: "company"; id: string; name: string; sub: string }
  | { type: "person"; id: string; name: string; sub: string }
  | { type: "document"; id: string; name: string; sub: string; cid: string };

interface Props {
  groupList: { id: string; name: string }[];
  currentGroupId: string;
  onChangeGroup: (id: string) => void;
  search: string;
  setSearch: (s: string) => void;
  onPickHit: (hit: SearchHit) => void;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  notifCount: number;
  onOpenNotif: () => void;
  user: { name?: string; email?: string; role?: string } | null;
  onProfile: () => void;
  onUsers: () => void;
  onSettings: () => void;
  onLogout: () => void;
}

function roleLabel(role?: string) {
  return role === "admin" ? "Admin" : role === "viewer" ? "Viewer" : "Konsultan";
}
function initials(name?: string) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

export default function TopHeader(props: Props) {
  const { groupList, currentGroupId, onChangeGroup, search, setSearch, onPickHit,
    theme, onToggleTheme, notifCount, onOpenNotif, user, onProfile, onUsers, onSettings, onLogout } = props;
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = search.trim().toLowerCase();
  const hits = useMemo(() => {
    if (!q) return { companies: [], persons: [], docs: [] } as { companies: SearchHit[]; persons: SearchHit[]; docs: SearchHit[] };
    const companies: SearchHit[] = [];
    for (const cid of COMPANY_ORDER) {
      const c = COMPANIES[cid];
      if (c.name.toLowerCase().includes(q) || c.short.toLowerCase().includes(q))
        companies.push({ type: "company", id: cid, name: c.name, sub: `Perusahaan · ${c.kedudukan}` });
    }
    const persons: SearchHit[] = [];
    for (const pid of PERSON_ORDER) {
      const p = PERSONS[pid];
      if (p.name.toLowerCase().includes(q))
        persons.push({ type: "person", id: pid, name: p.name, sub: "Individu / Pemegang saham" });
    }
    const docs: SearchHit[] = [];
    for (const cid of COMPANY_ORDER) {
      const c = COMPANIES[cid];
      for (let i = 0; i < c.documents.length; i++) {
        const d = c.documents[i];
        if (d.label.toLowerCase().includes(q))
          docs.push({ type: "document", id: `${cid}-${i}`, cid, name: d.label, sub: c.name });
      }
    }
    return { companies: companies.slice(0, 6), persons: persons.slice(0, 6), docs: docs.slice(0, 6) };
  }, [q]);

  const total = hits.companies.length + hits.persons.length + hits.docs.length;

  const pick = (hit: SearchHit) => { onPickHit(hit); setSearchOpen(false); };

  return (
    <header className="topbar" data-testid="topbar">
      <div className="topbar-group">
        <div className="topbar-group-ic"><Building2 size={19} strokeWidth={2} /></div>
        <div className="topbar-group-sel">
          <span className="topbar-group-lbl">Grup Klien</span>
          <div className="grp-select-wrap">
            {groupList.length > 0 ? (
              <select
                className="grp-select"
                value={currentGroupId}
                onChange={(e) => onChangeGroup(e.target.value)}
                data-testid="group-select"
                aria-label="Pilih grup klien"
              >
                {groupList.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            ) : (
              <span className="grp-select" data-testid="no-group">Tidak ada grup</span>
            )}
            <ChevronDown className="grp-caret" size={16} />
          </div>
        </div>
      </div>

      <div className="topbar-search" ref={searchRef}>
        <Search className="topbar-search-ic" size={17} />
        <input
          className="topbar-search-input"
          placeholder="Cari perusahaan, orang, atau dokumen..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setSearchOpen(true); }}
          onFocus={() => setSearchOpen(true)}
          data-testid="global-search"
        />
        {searchOpen && q && (
          <div className="search-results" data-testid="search-results">
            {total === 0 && <div className="search-empty">Tidak ada hasil untuk “{search}”.</div>}
            {hits.companies.length > 0 && <div className="search-group-lbl">Perusahaan</div>}
            {hits.companies.map((h) => (
              <button key={h.type + "-" + h.id} className="search-row" onClick={() => pick(h)} data-testid={`search-hit-company-${h.id}`}>
                <span className="search-row-ic"><Building2 size={16} /></span>
                <span><span className="search-row-main">{h.name}</span><br /><span className="search-row-sub">{h.sub}</span></span>
              </button>
            ))}
            {hits.persons.length > 0 && <div className="search-group-lbl">Individu</div>}
            {hits.persons.map((h) => (
              <button key={h.type + "-" + h.id} className="search-row" onClick={() => pick(h)} data-testid={`search-hit-person-${h.id}`}>
                <span className="search-row-ic"><UserIcon size={16} /></span>
                <span><span className="search-row-main">{h.name}</span><br /><span className="search-row-sub">{h.sub}</span></span>
              </button>
            ))}
            {hits.docs.length > 0 && <div className="search-group-lbl">Dokumen</div>}
            {hits.docs.map((h) => (
              <button key={h.type + "-" + h.id} className="search-row" onClick={() => pick(h)} data-testid={`search-hit-doc-${h.id}`}>
                <span className="search-row-ic"><FileText size={16} /></span>
                <span><span className="search-row-main">{h.name}</span><br /><span className="search-row-sub">{h.sub}</span></span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="topbar-right">
        <button className="icon-round" onClick={onOpenNotif} data-testid="notif-btn" title="Data issues">
          <Bell size={18} />
          {notifCount > 0 && <span className="notif-dot" />}
        </button>
        <button className="icon-round" onClick={onToggleTheme} data-testid="theme-toggle" title={theme === "dark" ? "Mode terang" : "Mode gelap"}>
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <div ref={menuRef} style={{ position: "relative" }}>
          <button className="user-chip" onClick={() => setMenuOpen((v) => !v)} data-testid="user-chip">
            <span className="user-avatar">{initials(user?.name)}</span>
            <span className="user-meta">
              <span className="user-meta-name">{user?.name}</span>
              <span className="user-meta-role">{roleLabel(user?.role)}</span>
            </span>
            <ChevronDown size={16} color="var(--tx-3)" />
          </button>
          {menuOpen && (
            <div className="user-menu" data-testid="user-menu">
              <div className="user-menu-head">
                <div className="n">{user?.name}</div>
                <div className="e">{user?.email}</div>
              </div>
              <button className="user-menu-item" onClick={() => { setMenuOpen(false); onProfile(); }} data-testid="menu-profile">
                <span className="um-ic"><UserIcon size={16} /></span> Profil
              </button>
              {user?.role === "admin" && (
                <button className="user-menu-item" onClick={() => { setMenuOpen(false); onUsers(); }} data-testid="menu-users">
                  <span className="um-ic"><Users size={16} /></span> Pengguna / Tim
                </button>
              )}
              <button className="user-menu-item" onClick={() => { setMenuOpen(false); onSettings(); }} data-testid="menu-settings">
                <span className="um-ic"><Settings size={16} /></span> Pengaturan
              </button>
              <button className="user-menu-item danger" onClick={() => { setMenuOpen(false); onLogout(); }} data-testid="logout-btn">
                <span className="um-ic"><LogOut size={16} /></span> Keluar
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
